import { isArray } from 'validate.js';
import {
  CATEGORY_FABRICS,
  CATEGORY_JACKETS,
  CATEGORY_SHIRTS,
  CATEGORY_SUITS,
  CATEGORY_THREE_PIECE_SUITS,
  CATEGORY_TROUSERS,
  CATEGORY_WAISTCOATS,
  SKU_3R2_JACKET,
  SKU_CLASSIC_DRESS_SHIRT,
  SKU_DB_JACKET,
  SKU_DB_OFFICE_SUIT,
  SKU_SB_JACKET,
  SKU_SB_OFFICE_SUIT,
  STR_CARDS,
  STR_RADIO,
} from '../../../../utils/constants';
import {
  PC_DEFAULT_NUMBER_OF_JACKET_BUTTONS,
  PC_DEFAULT_TROUSER_CUFFS,
  PC_OPTIONS_KEY_NUMBER_OF_JACKET_BUTTONS,
  PC_OPTIONS_KEY_TROUSER_CUFFS,
  PC_OPTIONS_KEY_JACKET_POCKET_STYLE,
  PC_DEFAULT_JACKET_POCKET_STYLE,
  PC_OPTIONS_KEY_DB_JACKET_BUTTONS,
  PC_DEFAULT_DB_JACKET_BUTTONS,
  PC_OPTIONS_KEY_DB_JACKET_POCKET_STYLE,
  PC_DEFAULT_DB_JACKET_POCKET_STYLE,
  PC_OPTIONS_KEY_DB_JACKET_NUMBER_OF_CUFF_BUTTONS,
  PC_DEFAULT_DB_JACKET_NUMBER_OF_CUFF_BUTTONS,
  PC_OPTIONS_KEY_SHIRT_CUFF,
  PC_DEFAULT_SHIRT_CUFF,
  PC_OPTIONS_KEY_SHIRT_CHEST_POCKET,
  PC_DEFAULT_SHIRT_CHEST_POCKET,
  PC_OPTIONS_KEY_SB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
  PC_DEFAULT_SB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
  PC_OPTIONS_KEY_SB_OFFICE_SUIT_POCKET_STYLE,
  PC_DEFAULT_SB_OFFICE_SUIT_POCKET_STYLE,
  PC_OPTIONS_KEY_SB_OFFICE_SUIT_TROUSER_CUFFS,
  PC_DEFAULT_SB_OFFICE_SUIT_TROUSER_CUFFS,
  PC_OPTIONS_KEY_DB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
  PC_OPTIONS_KEY_DB_OFFICE_SUIT_JACKET_BUTTONS,
  PC_OPTIONS_KEY_DB_OFFICE_SUIT_TROUSER_CUFFS,
  PC_DEFAULT_DB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
  PC_DEFAULT_DB_OFFICE_SUIT_JACKET_BUTTONS,
  PC_DEFAULT_DB_OFFICE_SUIT_TROUSER_CUFFS,
  PC_DEFAULT_3R2_NUMBER_OF_CUFF_BUTTONS,
  PC_DEFAULT_3R2_POCKET_STYLE,
  PC_OPTIONS_KEY_3R2_NUMBER_OF_CUFF_BUTTONS,
  PC_OPTIONS_KEY_3R2_POCKET_STYLE,
} from '../productCustomization.constants';

export const isTrousers = (categoryName) => categoryName === CATEGORY_TROUSERS;

// Jacket functions
export const isJackets = (categoryName) => categoryName === CATEGORY_JACKETS;
export const isSBJacketSKU = (sku) => sku === SKU_SB_JACKET;
export const isDBJacketSKU = (sku) => sku === SKU_DB_JACKET;
export const isThreeRollTwoJacketSKU = (sku) => sku === SKU_3R2_JACKET;

// Shirt functions
export const isShirts = (categoryName) => categoryName === CATEGORY_SHIRTS;
export const isClassicDressShirtSKU = (sku) => sku === SKU_CLASSIC_DRESS_SHIRT;
export const hasCuffOption = (sku) => isClassicDressShirtSKU(sku);

// Suit functions
export const isSuits = (categoryName) =>
  categoryName === CATEGORY_SUITS ||
  categoryName === CATEGORY_THREE_PIECE_SUITS;
export const isSBOfficeSuitSKU = (sku) => sku === SKU_SB_OFFICE_SUIT;
export const isDBOfficeSuitSKU = (sku) => sku === SKU_DB_OFFICE_SUIT;

// Fabric functions
export const isFabrics = (categoryName) => categoryName === CATEGORY_FABRICS;

// Waistcoat functions
export const isWaistcoats = (categoryName) =>
  categoryName === CATEGORY_WAISTCOATS;

// MTM
export const isMTMSKU = (sku) => {
  if (typeof sku !== 'string') return false;
  return sku.slice(0, 3) === 'MTM';
};

/**
 * Returns an array of objects denoting the list of options for a given category. Objects are in the form {key,value} where key is the option name and value is the default value
 * Eg:
 * [
 *  { key: Trouser Cuffs, value: true }
 * ]
 * @param {Object} product Product object
 * @returns Array of key/value objects
 */
export const getDefaultOptions = (product) => {
  return product?.options?.map((option) => ({
    key: option.title,
    value: option.defaultValue,
  }));
};

/**
 * Returns an array of objects denoting the list of options for a given category. Objects are in the form {key,value} where key is the option name and value is the default value
 * Eg:
 * [
 *  { key: trouserCuffs, value: true }
 * ]
 * @param {String} categoryName Name of a product category
 * @returns Array of key/value objects
 */
export const getDefaultOptions_deprecated = (categoryName, sku) => {
  const options = [];
  // For trousers, return [ { key:'trouserCuffs', value:true } ]
  if (isTrousers(categoryName)) {
    options.push({
      key: PC_OPTIONS_KEY_TROUSER_CUFFS,
      value: PC_DEFAULT_TROUSER_CUFFS,
    });
  }
  // For double jackets, return [ { key:'dbCoatButtons', value:6x2 }, {key:'dbPocketStyle', value:'Straight'}, {key:'dbCuffButtons', value:4} ]
  else if (isJackets(categoryName) && isDBJacketSKU(sku)) {
    options.push({
      key: PC_OPTIONS_KEY_DB_JACKET_BUTTONS,
      value: PC_DEFAULT_DB_JACKET_BUTTONS,
    });
    options.push({
      key: PC_OPTIONS_KEY_DB_JACKET_POCKET_STYLE,
      value: PC_DEFAULT_DB_JACKET_POCKET_STYLE,
    });
    options.push({
      key: PC_OPTIONS_KEY_DB_JACKET_NUMBER_OF_CUFF_BUTTONS,
      value: PC_DEFAULT_DB_JACKET_NUMBER_OF_CUFF_BUTTONS,
    });
  }
  // For jackets, return [ { key:'jacketButtons', value:3 }, {key:'pocketStyle', value:'Slanted'} ]
  else if (isJackets(categoryName) && isSBJacketSKU(sku)) {
    options.push({
      key: PC_OPTIONS_KEY_NUMBER_OF_JACKET_BUTTONS,
      value: PC_DEFAULT_NUMBER_OF_JACKET_BUTTONS,
    });
    options.push({
      key: PC_OPTIONS_KEY_JACKET_POCKET_STYLE,
      value: PC_DEFAULT_JACKET_POCKET_STYLE,
    });
  }
  // For 3R2 jackets, return [ { key:'threeRollTwoCuffButtons', value:4 }, {key:'threeRollTwoPocketStyle', value:'Straight'} ]
  else if (isJackets(categoryName) && isThreeRollTwoJacketSKU(sku)) {
    options.push({
      key: PC_OPTIONS_KEY_3R2_NUMBER_OF_CUFF_BUTTONS,
      value: PC_DEFAULT_3R2_NUMBER_OF_CUFF_BUTTONS,
    });
    options.push({
      key: PC_OPTIONS_KEY_3R2_POCKET_STYLE,
      value: PC_DEFAULT_3R2_POCKET_STYLE,
    });
  }
  // For Shirts
  else if (isShirts(categoryName)) {
    // cuff option is only needed for classic dress shirt
    if (hasCuffOption(sku)) {
      options.push({
        key: PC_OPTIONS_KEY_SHIRT_CUFF,
        value: PC_DEFAULT_SHIRT_CUFF,
      });
    }

    // chest pocket option is needed for ALL shirt SKUs
    options.push({
      key: PC_OPTIONS_KEY_SHIRT_CHEST_POCKET,
      value: PC_DEFAULT_SHIRT_CHEST_POCKET,
    });
  }

  // For SB Office Suit
  else if (isSBOfficeSuitSKU(sku)) {
    options.push({
      key: PC_OPTIONS_KEY_SB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
      value: PC_DEFAULT_SB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
    });
    options.push({
      key: PC_OPTIONS_KEY_SB_OFFICE_SUIT_POCKET_STYLE,
      value: PC_DEFAULT_SB_OFFICE_SUIT_POCKET_STYLE,
    });
    options.push({
      key: PC_OPTIONS_KEY_SB_OFFICE_SUIT_TROUSER_CUFFS,
      value: PC_DEFAULT_SB_OFFICE_SUIT_TROUSER_CUFFS,
    });
  }

  // For DB Office Suit
  else if (isDBOfficeSuitSKU(sku)) {
    options.push({
      key: PC_OPTIONS_KEY_DB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
      value: PC_DEFAULT_DB_OFFICE_SUIT_NUMBER_OF_CUFF_BUTTONS,
    });
    options.push({
      key: PC_OPTIONS_KEY_DB_OFFICE_SUIT_JACKET_BUTTONS,
      value: PC_DEFAULT_DB_OFFICE_SUIT_JACKET_BUTTONS,
    });
    options.push({
      key: PC_OPTIONS_KEY_DB_OFFICE_SUIT_TROUSER_CUFFS,
      value: PC_DEFAULT_DB_OFFICE_SUIT_TROUSER_CUFFS,
    });
  }

  return options;
};

/**
 * Returns array of strings where input values are numbers
 * Used by NumericOption to build radio options
 *
 * input: [1,2,3]
 * output: ['1','2','3']
 *
 * @param {Array} array
 * @returns Array
 */
export const getNumericArrayAsStringArray = (array) => {
  if (!isArray(array)) return [];

  return array.map((ele) => ele.toString());
};

/**
 * Returns true if given option's type is STR_RADIO (radio)
 * Used by OptionsCustomizer
 *
 * @param {String} option
 * @returns Boolean
 */
export const isRadioOption = (option) => option?.type === STR_RADIO;

/**
 * Returns true if given option's type is STR_CARDS (cards)
 * Used by OptionsCustomizer
 *
 * @param {String} option
 * @returns Boolean
 */
export const isCardsOption = (option) => option?.type === STR_CARDS;
